/**
 * IndexedDB storage quota monitoring and preemptive cleanup.
 *
 * iOS WebView aggressively evicts IndexedDB under storage pressure with no
 * warning to the app — entire stores can vanish. This service polls
 * navigator.storage.estimate() and purges the lowest-value cached data
 * (synced photos, server-cached blobs, old orthophoto tiles) before
 * the OS does it for us. Unsynced data is never touched.
 */
import { db } from './db';

const WARN_THRESHOLD = 0.70;
const CLEANUP_THRESHOLD = 0.85;
const TARGET_AFTER_CLEANUP = 0.60;

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const SYNCED_PHOTO_MIN_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const CACHED_PHOTO_MIN_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAP_FEATURE_MIN_AGE_MS = 24 * 60 * 60 * 1000;

export interface StorageQuotaStatus {
  usageBytes: number;
  quotaBytes: number;
  percentUsed: number;
  available: boolean;
  isWarning: boolean;
  isCritical: boolean;
}

export interface CleanupResult {
  freedBytes: number;
  itemsDeleted: number;
  steps: Array<{ table: string; deleted: number }>;
}

type StatusListener = (status: StorageQuotaStatus) => void;

const UNAVAILABLE: StorageQuotaStatus = {
  usageBytes: 0,
  quotaBytes: 0,
  percentUsed: 0,
  available: false,
  isWarning: false,
  isCritical: false,
};

function toEpochMs(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : 0;
  }
  return 0;
}

function isSyncedTruthy(value: unknown): boolean {
  return value === true || value === 1;
}

class StorageQuotaService {
  private listeners = new Set<StatusListener>();
  private currentStatus: StorageQuotaStatus | null = null;
  private intervalHandle: ReturnType<typeof setInterval> | null = null;
  private cleanupInFlight: Promise<CleanupResult> | null = null;

  async checkQuota(): Promise<StorageQuotaStatus> {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
      this.currentStatus = UNAVAILABLE;
      return UNAVAILABLE;
    }
    try {
      const { usage = 0, quota = 0 } = await navigator.storage.estimate();
      const percent = quota > 0 ? usage / quota : 0;
      const status: StorageQuotaStatus = {
        usageBytes: usage,
        quotaBytes: quota,
        percentUsed: percent,
        available: true,
        isWarning: percent >= WARN_THRESHOLD,
        isCritical: percent >= CLEANUP_THRESHOLD,
      };
      this.currentStatus = status;
      this.notifyListeners(status);
      return status;
    } catch (err) {
      console.warn('[StorageQuota] estimate() failed:', err);
      this.currentStatus = UNAVAILABLE;
      return UNAVAILABLE;
    }
  }

  getStatus(): StorageQuotaStatus | null {
    return this.currentStatus;
  }

  async cleanupIfNeeded(): Promise<CleanupResult> {
    if (this.cleanupInFlight) return this.cleanupInFlight;
    const status = await this.checkQuota();
    if (!status.available || !status.isCritical) {
      return { freedBytes: 0, itemsDeleted: 0, steps: [] };
    }
    this.cleanupInFlight = this.runCleanup(status).finally(() => {
      this.cleanupInFlight = null;
    });
    return this.cleanupInFlight;
  }

  private async runCleanup(initial: StorageQuotaStatus): Promise<CleanupResult> {
    const steps: Array<{ table: string; deleted: number }> = [];
    let totalDeleted = 0;
    const now = Date.now();

    const isStillCritical = async (): Promise<boolean> => {
      const status = await this.checkQuota();
      return status.percentUsed > TARGET_AFTER_CLEANUP;
    };

    // Step 1: synced damage-assessment photos older than 7d (biggest blobs)
    try {
      const photoCutoff = now - SYNCED_PHOTO_MIN_AGE_MS;
      const ids = await db.offlinePhotos
        .filter(p => isSyncedTruthy(p.is_synced) && toEpochMs(p.created_at) < photoCutoff)
        .primaryKeys();
      if (ids.length > 0) {
        await db.offlinePhotos.bulkDelete(ids);
        steps.push({ table: 'offlinePhotos', deleted: ids.length });
        totalDeleted += ids.length;
        if (!(await isStillCritical())) {
          return this.finalize(initial, steps, totalDeleted);
        }
      }
    } catch (err) {
      console.warn('[StorageQuota] offlinePhotos cleanup failed:', err);
    }

    // Step 2: cached reference photos (contacts/credentials/members) older than 7d
    try {
      const cachedPhotoCutoff = now - CACHED_PHOTO_MIN_AGE_MS;
      const ids = await db.cachedPhotos
        .filter(p => toEpochMs(p.cached_at) < cachedPhotoCutoff)
        .primaryKeys();
      if (ids.length > 0) {
        await db.cachedPhotos.bulkDelete(ids);
        steps.push({ table: 'cachedPhotos', deleted: ids.length });
        totalDeleted += ids.length;
        if (!(await isStillCritical())) {
          return this.finalize(initial, steps, totalDeleted);
        }
      }
    } catch (err) {
      console.warn('[StorageQuota] cachedPhotos cleanup failed:', err);
    }

    // Step 3: synced drone snapshots older than 7d (large JPEG blobs)
    try {
      const snapshotCutoff = now - SYNCED_PHOTO_MIN_AGE_MS;
      const ids = await db.offlineSnapshots
        .filter(s => isSyncedTruthy(s.is_synced) && toEpochMs(s.captured_at) < snapshotCutoff)
        .primaryKeys();
      if (ids.length > 0) {
        await db.offlineSnapshots.bulkDelete(ids);
        steps.push({ table: 'offlineSnapshots', deleted: ids.length });
        totalDeleted += ids.length;
        if (!(await isStillCritical())) {
          return this.finalize(initial, steps, totalDeleted);
        }
      }
    } catch (err) {
      console.warn('[StorageQuota] offlineSnapshots cleanup failed:', err);
    }

    // Step 4: cached map features older than 24h (re-fetchable from server)
    try {
      const featureCutoff = now - MAP_FEATURE_MIN_AGE_MS;
      const ids = await db.cachedMapFeatures
        .filter(f => isSyncedTruthy(f.is_synced) && toEpochMs(f.cached_at) < featureCutoff)
        .primaryKeys();
      if (ids.length > 0) {
        await db.cachedMapFeatures.bulkDelete(ids);
        steps.push({ table: 'cachedMapFeatures', deleted: ids.length });
        totalDeleted += ids.length;
        if (!(await isStillCritical())) {
          return this.finalize(initial, steps, totalDeleted);
        }
      }
    } catch (err) {
      console.warn('[StorageQuota] cachedMapFeatures cleanup failed:', err);
    }

    // Step 5: orthophoto tiles from oldest regions (large blobs, no per-tile age)
    try {
      const regions = await db.cachedOrthophotoRegions
        .orderBy('created_at')
        .toArray();
      let deleted = 0;
      for (const region of regions) {
        const tileIds = await db.cachedOrthophotoTiles
          .where('region_id').equals(region.id)
          .primaryKeys();
        if (tileIds.length > 0) {
          await db.cachedOrthophotoTiles.bulkDelete(tileIds);
          await db.cachedOrthophotoRegions.delete(region.id);
          deleted += tileIds.length;
          if (!(await isStillCritical())) break;
        }
      }
      if (deleted > 0) {
        steps.push({ table: 'cachedOrthophotoTiles', deleted });
        totalDeleted += deleted;
        if (!(await isStillCritical())) {
          return this.finalize(initial, steps, totalDeleted);
        }
      }
    } catch (err) {
      console.warn('[StorageQuota] cachedOrthophotoTiles cleanup failed:', err);
    }

    // (There is no basemap-tile step: the offline basemap tile tables were
    // dropped in Dexie version 32, P0-20.)

    return this.finalize(initial, steps, totalDeleted);
  }

  private async finalize(
    initial: StorageQuotaStatus,
    steps: Array<{ table: string; deleted: number }>,
    itemsDeleted: number
  ): Promise<CleanupResult> {
    const after = await this.checkQuota();
    const freedBytes = Math.max(0, initial.usageBytes - after.usageBytes);
    if (itemsDeleted > 0) {
      console.info(
        `[StorageQuota] cleanup freed ${(freedBytes / 1024 / 1024).toFixed(1)} MB ` +
        `across ${itemsDeleted} items (${(after.percentUsed * 100).toFixed(0)}% used)`
      );
    }
    return { freedBytes, itemsDeleted, steps };
  }

  startMonitoring(): void {
    if (this.intervalHandle) return;
    void this.tick();
    this.intervalHandle = setInterval(() => {
      void this.tick();
    }, CHECK_INTERVAL_MS);
  }

  stopMonitoring(): void {
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
  }

  private async tick(): Promise<void> {
    const status = await this.checkQuota();
    if (status.isCritical) {
      await this.cleanupIfNeeded();
    }
  }

  addListener(cb: StatusListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private notifyListeners(status: StorageQuotaStatus): void {
    this.listeners.forEach(cb => {
      try {
        cb(status);
      } catch (err) {
        console.warn('[StorageQuota] listener error:', err);
      }
    });
  }
}

export const storageQuotaService = new StorageQuotaService();
